import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAccessibleCertifications } from "@/hooks/useAccessibleCertifications";
import { useActiveCertificationSelfHeal } from "@/hooks/useActiveCertificationSelfHeal";

import LockedPageOverlay, { lockedCopyForPath } from "@/components/LockedPageOverlay";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-secondary">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

/**
 * Route guard for certification scoped pages.
 *
 * Access is per certification. The guard checks the certification actually
 * in scope: the `:certificationId` route param when the route has one,
 * otherwise the learner's active certification. Rows that are revoked or
 * past `expires_at` do not count (see `useAccessibleCertifications`).
 *
 * Pages inside the app shell render behind a locked overlay instead of
 * bouncing the user, so a free account can see what a plan unlocks.
 * Full screen routes (exam, practice, keyword practice, results) have no
 * sidebar and no meaningful preview, so they redirect to /dashboard.
 *
 * Admins and super admins bypass, same as PaidRoute.
 */
export const RequireCertification = ({
  mode = "lock",
}: {
  mode?: "lock" | "redirect";
}) => {
  const { user, profile, isAdmin, isSuperAdmin, isLoading } = useAuth();
  const { accessibleIds, isLoaded, isStale, hasLoadedOnce } = useAccessibleCertifications();
  const location = useLocation();
  const params = useParams();
  // If the stored active certification lapsed but another one is still valid,
  // switch to it and persist the correction.
  const { effectiveActiveCertId, isHealing } = useActiveCertificationSelfHeal(
    accessibleIds,
    isLoaded,
    isStale,
  );

  if (isLoading || (user && !isLoaded) || isHealing) {
    return <Spinner />;
  }

  if (isAdmin || isSuperAdmin) {
    return <Outlet />;
  }

  // Certification in scope for this route.
  const scopedCertId = params.certificationId ?? effectiveActiveCertId ?? profile?.active_certification_id ?? null;
  const allowed = scopedCertId
    ? accessibleIds.includes(scopedCertId)
    : accessibleIds.length > 0;

  // A failed refresh means "we do not know", not "access lost". Keep the page
  // rendered rather than bouncing a learner out of an in-progress session.
  // Only when we have a confirmed baseline: if the very first check failed we
  // have nothing to preserve, so fall back to the safe (locked) behaviour.
  if (!allowed && isStale && hasLoadedOnce) {
    return <Outlet />;
  }


  if (!allowed) {
    if (mode === "redirect") {
      return <Navigate to="/dashboard" replace state={{ certAccessLost: true }} />;
    }
    const copy = lockedCopyForPath(location.pathname);
    return (
      <LockedPageOverlay pageName={copy.name} blurb={copy.blurb} />
    );
  }


  return <Outlet />;
};

export default RequireCertification;
